import { z } from "zod";
import type {
  ApplicationRole,
  PermissionDescriptor,
  PermissionKey,
  SystemRoleKey,
} from "@yazamco/shared-types";

/**
 * The centralized, typed permission catalogue — the single source of truth for
 * `<resource>.<action>` permission keys shared safely between the API and the
 * frontend. Never scatter raw permission string literals through the codebase;
 * reference `PERMISSION_KEYS` / the `permissionKeySchema` instead.
 */
export const PERMISSION_CATALOGUE = [
  { key: "dashboard.read", resource: "dashboard", action: "read", category: "Dashboard", description: "View the operations dashboard" },

  { key: "users.read", resource: "users", action: "read", category: "Identity", description: "View platform users" },
  { key: "users.create", resource: "users", action: "create", category: "Identity", description: "Invite / create users" },
  { key: "users.update", resource: "users", action: "update", category: "Identity", description: "Edit user profile & status" },
  { key: "users.disable", resource: "users", action: "disable", category: "Identity", description: "Suspend or disable users" },
  { key: "users.delete", resource: "users", action: "delete", category: "Identity", description: "Soft-delete users" },

  { key: "roles.read", resource: "roles", action: "read", category: "Identity", description: "View roles & permissions" },
  { key: "roles.create", resource: "roles", action: "create", category: "Identity", description: "Create custom roles" },
  { key: "roles.update", resource: "roles", action: "update", category: "Identity", description: "Edit roles & their permissions" },
  { key: "roles.delete", resource: "roles", action: "delete", category: "Identity", description: "Delete custom roles" },
  { key: "roles.assign", resource: "roles", action: "assign", category: "Identity", description: "Assign / remove user roles" },

  { key: "sessions.read", resource: "sessions", action: "read", category: "Identity", description: "View active sessions" },
  { key: "sessions.revoke", resource: "sessions", action: "revoke", category: "Identity", description: "Revoke sessions" },

  { key: "audit.read", resource: "audit", action: "read", category: "Governance", description: "Read the audit log" },
  { key: "settings.read", resource: "settings", action: "read", category: "Governance", description: "View platform settings" },
  { key: "settings.update", resource: "settings", action: "update", category: "Governance", description: "Change platform settings" },

  { key: "servers.read", resource: "servers", action: "read", category: "Operations", description: "View servers" },
  { key: "servers.manage", resource: "servers", action: "manage", category: "Operations", description: "Manage servers & tags" },
  { key: "containers.read", resource: "containers", action: "read", category: "Operations", description: "View containers" },
  { key: "containers.manage", resource: "containers", action: "manage", category: "Operations", description: "Refresh / manage containers" },
  { key: "customers.read", resource: "customers", action: "read", category: "Operations", description: "View customers" },
  { key: "customers.manage", resource: "customers", action: "manage", category: "Operations", description: "Provision / edit customers" },
  { key: "services.read", resource: "services", action: "read", category: "Operations", description: "View the service (systems) catalog" },
  { key: "services.manage", resource: "services", action: "manage", category: "Operations", description: "Add / edit / remove catalog services" },
  { key: "jenkins.read", resource: "jenkins", action: "read", category: "Operations", description: "View Jenkins jobs" },
  { key: "jenkins.trigger", resource: "jenkins", action: "trigger", category: "Operations", description: "Trigger Jenkins jobs / sync" },
  { key: "reports.read", resource: "reports", action: "read", category: "Operations", description: "View & run reports" },
  { key: "reports.create", resource: "reports", action: "create", category: "Operations", description: "Create report templates" },
  { key: "webhooks.read", resource: "webhooks", action: "read", category: "Operations", description: "View webhooks" },
  { key: "webhooks.manage", resource: "webhooks", action: "manage", category: "Operations", description: "Manage webhooks" },
  { key: "onboarding.read", resource: "onboarding", action: "read", category: "Operations", description: "View M365 onboarding" },
  { key: "onboarding.manage", resource: "onboarding", action: "manage", category: "Operations", description: "Run M365 onboarding" },

  // Customer Audits. Split out from customers.* so a machine credential that
  // only needs audit results does not also gain the customer directory.
  { key: "audits.read", resource: "audits", action: "read", category: "Operations", description: "View customer audit runs & reports" },
  { key: "audits.run", resource: "audits", action: "run", category: "Operations", description: "Enqueue audit runs & refresh audit data" },
] as const satisfies readonly PermissionDescriptor[];

/** All permission keys, as a readonly tuple (source of truth for the union). */
export const PERMISSION_KEYS = PERMISSION_CATALOGUE.map((p) => p.key) as [
  PermissionKey,
  ...PermissionKey[],
];

const PERMISSION_KEY_SET = new Set<string>(PERMISSION_KEYS);

/** True when `key` is a recognized permission key. */
export function isPermissionKey(key: string): key is PermissionKey {
  return PERMISSION_KEY_SET.has(key);
}

/** Zod schema accepting exactly one catalogue permission key. */
export const permissionKeySchema = z.enum(PERMISSION_KEYS);

/** Operational reads granted to the lowest privileged operational role. */
const OPERATIONAL_READS: PermissionKey[] = [
  "dashboard.read",
  "servers.read",
  "containers.read",
  "customers.read",
  "services.read",
  "jenkins.read",
  "reports.read",
  "webhooks.read",
  "onboarding.read",
  // Audit reads were previously gated by requireRole("Viewer"); keeping them in
  // the baseline read set preserves that behavior exactly.
  "audits.read",
];

const OPERATOR_MANAGE: PermissionKey[] = [
  "servers.manage",
  "containers.manage",
  "customers.manage",
  "jenkins.trigger",
  "reports.create",
  "webhooks.manage",
  "onboarding.manage",
  // Audit runs were previously gated by requireRole("Operator").
  "audits.run",
];

const GOVERNANCE_READS: PermissionKey[] = [
  "audit.read",
  "settings.read",
  "users.read",
  "roles.read",
  "sessions.read",
];

const ALL_PERMISSIONS: PermissionKey[] = PERMISSION_KEYS.slice();

/**
 * Built-in system roles and their permission sets. `super_admin` holds every
 * permission; `admin` holds everything except the ability to delete roles (the
 * final recovery guard). These are seeded by migration 0013 and protected from
 * deletion.
 */
export const SYSTEM_ROLES: Record<
  SystemRoleKey,
  { name: string; description: string; permissions: PermissionKey[] }
> = {
  super_admin: {
    name: "Super Administrator",
    description: "Full, unrestricted access to every feature and setting.",
    permissions: ALL_PERMISSIONS,
  },
  admin: {
    name: "Administrator",
    description: "Full administration except deleting roles.",
    permissions: ALL_PERMISSIONS.filter((p) => p !== "roles.delete"),
  },
  operator: {
    name: "Operator",
    description: "Run operational actions across all modules; no identity administration.",
    permissions: [...OPERATIONAL_READS, ...OPERATOR_MANAGE, "audit.read"],
  },
  auditor: {
    name: "Auditor",
    description: "Read-only across operations plus the audit log and identity.",
    permissions: [...OPERATIONAL_READS, ...GOVERNANCE_READS],
  },
  read_only: {
    name: "Read Only",
    description: "Read-only access to operational modules.",
    permissions: [...OPERATIONAL_READS],
  },
};

/**
 * Maps the pre-existing hierarchical application role to the implicit system
 * role a user holds by virtue of it. This is what reproduces today's behavior
 * additively: the effective permission baseline for a user with only a
 * hierarchical role (no explicit RBAC assignments) equals this set. The
 * Administrator role maps to the full permission set so existing
 * Administrator-guarded flows (and dev/test) can exercise the admin APIs.
 */
export function permissionsForApplicationRole(
  role: ApplicationRole,
): PermissionKey[] {
  switch (role) {
    case "Administrator":
      return SYSTEM_ROLES.super_admin.permissions.slice();
    case "Operator":
      return SYSTEM_ROLES.operator.permissions.slice();
    case "Viewer":
      return SYSTEM_ROLES.read_only.permissions.concat("audit.read");
  }
}

/** Union the derived-from-role baseline with any explicitly-assigned perms. */
export function effectivePermissions(
  roles: readonly ApplicationRole[],
  assigned: readonly PermissionKey[] = [],
): PermissionKey[] {
  const set = new Set<PermissionKey>(assigned);
  for (const role of roles) {
    for (const p of permissionsForApplicationRole(role)) set.add(p);
  }
  return [...set];
}

// ── User lifecycle status ───────────────────────────────────────────────────
export const USER_STATUSES = [
  "invited",
  "active",
  "suspended",
  "disabled",
  "deleted",
] as const;
export const userStatusSchema = z.enum(USER_STATUSES);

// ── Bounded pagination ───────────────────────────────────────────────────────
export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 25;

/** Query schema for bounded, server-side paginated list endpoints. */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  search: z.string().trim().max(200).optional(),
});

export const usersListQuerySchema = paginationQuerySchema.extend({
  status: userStatusSchema.optional(),
  roleId: z.string().uuid().optional(),
  sort: z.enum(["createdAt", "email", "displayName", "lastLoginAt", "status"]).default("createdAt"),
  direction: z.enum(["asc", "desc"]).default("desc"),
});

// ── Administrative mutation schemas ──────────────────────────────────────────
/**
 * Profile edit only. `status` is deliberately ABSENT: a lifecycle change is a
 * different authorization decision (`users.disable`, not `users.update`) and has
 * its own endpoint. Because the schema is `.strict()`, sending `status` here is
 * rejected rather than silently ignored — so the split cannot be bypassed by
 * mass assignment.
 */
export const updateUserSchema = z
  .object({
    displayName: z.string().trim().min(1).max(200).optional(),
    /** Optimistic concurrency: the version the client last read. */
    version: z.number().int().nonnegative(),
  })
  .strict();

/** PUT /users/:id/status — the lifecycle transition, on its own permission. */
export const updateUserStatusSchema = z
  .object({
    status: z.enum(["invited", "active", "suspended", "disabled"]),
    version: z.number().int().nonnegative(),
  })
  .strict();

export const assignRoleSchema = z
  .object({ roleId: z.string().uuid() })
  .strict();

/** Create a pending identity and attach its access groups before first sign-in. */
export const inviteUserSchema = z
  .object({
    email: z.string().trim().email().max(320).transform((value) => value.toLowerCase()),
    displayName: z.string().trim().min(1).max(200).optional(),
    roleIds: z.array(z.string().uuid()).max(20).default([]),
  })
  .strict();

export const createRoleSchema = z
  .object({
    key: z
      .string()
      .trim()
      .min(2)
      .max(64)
      .regex(/^[a-z][a-z0-9_]*$/, "key must be lower_snake_case"),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional(),
    permissions: z.array(permissionKeySchema).max(PERMISSION_KEYS.length).default([]),
  })
  .strict();

export const updateRoleSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    isEnabled: z.boolean().optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();

export const setRolePermissionsSchema = z
  .object({
    permissions: z.array(permissionKeySchema).max(PERMISSION_KEYS.length),
    version: z.number().int().nonnegative(),
  })
  .strict();

export const duplicateRoleSchema = z
  .object({
    key: z
      .string()
      .trim()
      .min(2)
      .max(64)
      .regex(/^[a-z][a-z0-9_]*$/, "key must be lower_snake_case"),
    name: z.string().trim().min(1).max(120),
  })
  .strict();

export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type UpdateUserStatusInput = z.infer<typeof updateUserStatusSchema>;
export type InviteUserInput = z.infer<typeof inviteUserSchema>;
export type CreateRoleInput = z.infer<typeof createRoleSchema>;
export type UpdateRoleInput = z.infer<typeof updateRoleSchema>;
export type SetRolePermissionsInput = z.infer<typeof setRolePermissionsSchema>;
export type UsersListQuery = z.infer<typeof usersListQuerySchema>;
